import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaUserPlus } from "react-icons/fa6";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input, Table } from "reactstrap";
import { IS_CHILD } from "../../../graphql/org-gql";
import useCheckGroup, { CheckGroup } from "../../../hook/useCheckGroup";
import useSecurityRoles from "../../../hook/useSecurityRoles";
import useToggle from "../../../hook/useToggle";
import { notifySuccess, notifyWarning } from "../../../services/notification";
import { ISecurityRole } from "../../../types/auth";
import { UserRoleType } from "../../../types/constants";
import { Action } from "../../../types/reducer-action";
import { IAssignedRole } from "../../../types/user";
import AutoFocusDiv from "../../common/auto-focus-div";
import confirm from "../../common/confirm";
import { DataEntryEvent } from "../../common/data-entry-reducer";
import { CancelIcon, DeleteIcon } from "../../common/icons";
import InlineFeedback from "../../common/inline-feedback";
import OmsToolTip from "../../common/oms-tooltip/oms-tooltip";
import SpinnerOverlay from "../../common/spinner/spinner";
import TableHeaders, { TableHeaderInfo } from "../../common/table-header";
import OrgSelectionPanel from "./org-selection-panel";
import './style.scss';
import { useUserDispatch, useUserState } from "./user-manager-context";
import useUserApi from "./useUserApi";
import Button from "../../common/button";

export default function UserRolesPanel() {
  const { form, errors } = useUserState();
  const dispatch = useUserDispatch();

  const assignedRoles = form.assignedRoles ?? [];

  const [isChildOrg] = useLazyQuery<{ isChild: boolean }>(IS_CHILD);

  const [securityRoles, getSecurityRole] = useSecurityRoles();

  const [selectedRole, setSelectedRole] = useState<ISecurityRole>();

  const { isOpen: isDropdownOpen, toggle: toggleDropdown } = useToggle();

  const { checkGroup } = useCheckGroup<string>();

  const { addUserRoles, removeUserRoles, busy } = useUserApi();

  useEffect(() => {
    checkGroup.init(assignedRoles.map(assignedRole => assignedRole.key))
  }, [assignedRoles]);

  const setAssignedRoles = (newRoles: IAssignedRole[]) => dispatch(
    new Action(DataEntryEvent.UpdateForm, { assignedRoles: newRoles })
  );

  const isAddingChildScope = async (assignedRoles: IAssignedRole[], newRole: IAssignedRole) => {
    if (!newRole.orgScope?.id) return false;

    const sameRoles = assignedRoles.filter(item => item.userRoleTypeId === newRole.userRoleTypeId);
    if (sameRoles.length === 0) return false;

    const existingScopes = sameRoles.map(item => item.orgScope?.id)
      .filter(id => id);
    if (existingScopes.includes(newRole.orgScope?.id)) return true;

    return isChildOrg({
      variables: {
        parentIds: existingScopes,
        childId: newRole.orgScope?.id
      }
    }).then(response => response.data?.isChild);
  };

  const isAddingParentScope = async (assignedRoles: IAssignedRole[], newRole: IAssignedRole) => {
    if (!newRole.orgScope?.id) return false;

    const existingScopes = assignedRoles.filter(item => item.userRoleTypeId === newRole.userRoleTypeId)
      .map(item => item.orgScope?.id)
      .filter(id => id);
    if (existingScopes.length === 0) return false;

    const queries = existingScopes.map(scope =>
      isChildOrg({
        variables: {
          parentIds: [newRole.orgScope?.id],
          childId: scope
        }
      }).then(response => !!response.data?.isChild)
    );

    return Promise.all(queries)
      .then(results => results.some(result => result))
  };

  const addRole = useCallback((role: IAssignedRole) => {
    if (form.id) {
      addUserRoles(form.id, [role])
        .then(ok => {
          if (ok) {
            notifySuccess();
          } else {
            notifyWarning('The changes is not saved.', 5000);
          }
        });
    } else {
      setAssignedRoles([...form.assignedRoles, role]);
    }
  }, [form]);

  const removeRoles = useCallback((roles: IAssignedRole[]) => {
    if (form.id) {
      removeUserRoles(form.id, roles)
        .then(ok => {
          if (ok) {
            notifySuccess();
          } else {
            notifyWarning('The changes is not saved.', 5000);
          }
        });
    } else {
      const remainingRoles = form.assignedRoles
        .filter(ar => roles.every(r => r.userRoleTypeId !== ar.userRoleTypeId && r.orgScope?.id !== ar.orgScope?.id));
      setAssignedRoles([...remainingRoles]);
    }
  }, [form]);

  const handleAssignRole = useCallback((newRole: IAssignedRole | undefined) => {
    if (!newRole) {
      setSelectedRole(undefined);
      return;
    }

    // compute the unique key
    newRole.key = `${newRole.userRoleTypeId}_${newRole.orgScope?.id ?? 0}`;

    isAddingChildScope(assignedRoles, newRole)
      .then(addingChild => {
        if (addingChild) {
          dispatch(new Action(DataEntryEvent.AddErrors,
            { assignedRoles: 'This organization scope is already assigned to the user.' })
          )
        } else {
          isAddingParentScope(assignedRoles, newRole)
            .then(addingParent => {
              const fnAddRole = () => addRole(newRole);

              if (addingParent) {
                confirm({
                  title: 'Overlapping scopes',
                  content: "Please note that the user's existing scope includes child organizations of the new scope you are assigning to the user.",
                  okText: '', // hide OK button
                  cancelText: 'OK',
                  titleCenter: true,
                  bodyCenter: true,
                  onHide: fnAddRole
                });
              } else {
                fnAddRole();
              }
            });
        }
      })
      .finally(() => setSelectedRole(undefined));
  }, [assignedRoles]);

  const handleRemoveRoles = useCallback(() => {
    const roles = form.assignedRoles.filter(role => checkGroup.isChecked(role.key));
    const fnRemoveRoles = () => removeRoles(roles);
    if (form.id) {
      confirm({
        title: 'Remove selected roles?',
        content: "Are you sure you want to remove the selected roles?",
        okText: <><DeleteIcon /> Remove</>,
        okBtnColor: 'danger',
        cancelText: <><CancelIcon /> Cancel</>,
        titleCenter: true,
        bodyCenter: true
      }).then(fnRemoveRoles);
    } else {
      fnRemoveRoles();
    }
  }, [form, checkGroup]);

  const handleSelectRole = useCallback((role: ISecurityRole) => {
    if ((role.name === UserRoleType.FRENCHTRANSLATOR && assignedRoles.some(item => getSecurityRole(item.userRoleTypeId)?.name === UserRoleType.COORDINATOR))
      || (role.name === UserRoleType.COORDINATOR && assignedRoles.some(item => getSecurityRole(item.userRoleTypeId)?.name === UserRoleType.FRENCHTRANSLATOR))) {
      dispatch(new Action(DataEntryEvent.AddErrors,
        { assignedRoles: 'A user cannot be granted both the Coordinator and French Translator role simultaneously.' })
      );
      return;
    }

    setSelectedRole(role);

    !role.scopeRole && handleAssignRole({ userRoleTypeId: role.id } as IAssignedRole);
  }, [securityRoles, assignedRoles]);

  if (!assignedRoles) return <></>;

  return (
    <div className="oms-container">
      <div className="oms-row">
        <Dropdown isOpen={isDropdownOpen} toggle={toggleDropdown} disabled={busy}>
          <DropdownToggle caret color="primary"><FaUserPlus /> Assign Role</DropdownToggle>
          <DropdownMenu style={{ overflowY: 'auto' }}>
            {securityRoles.map((role, index) =>
              <DropdownItem key={role.id ?? index} onClick={() => handleSelectRole(role)}
                disabled={!role.scopeRole && assignedRoles.some(r => r?.userRoleTypeId === role.id)}>
                {role.desc}
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>

        <Button type="button" aria-label="Remove Role" color="danger"
          onClick={handleRemoveRoles} disabled={busy || checkGroup.isNoneChecked()}>
          <DeleteIcon /> Remove Role
        </Button>

        {busy && <SpinnerOverlay />}
      </div>

      {selectedRole?.scopeRole &&
        <AutoFocusDiv>
          <OrgSelectionPanel securityRole={selectedRole} onComplete={handleAssignRole} />
        </AutoFocusDiv>
      }

      <UserRolesTable roles={assignedRoles} checkGroup={checkGroup} />

      <InlineFeedback invalid={!!errors.assignedRoles} className="mt-0">
        {errors?.assignedRoles}
      </InlineFeedback>
    </div>
  );
}

interface RoleInfo extends IAssignedRole {
  roleName: string;
  orgName: string;
}

const TABLE_HEADERS: TableHeaderInfo<RoleInfo>[] = [
  { header: 'Role', accessor: 'roleName', presorted: 'asc' },
  { header: 'Org/Group', accessor: 'orgName', sortable: true }
];

const UserRolesTable = (props: {
  roles: IAssignedRole[],
  checkGroup: CheckGroup<string>
}) => {
  const { roles, checkGroup } = props;

  const [, getSecurityRole] = useSecurityRoles();

  const tableHeaders = [
    {
      header: <Input id="selectAll" type="checkbox"
        title="Select all" aria-label="Select all"
        checked={checkGroup.isAllChecked()}
        onChange={checkGroup.onCheckAll}
        disabled={checkGroup.isEmpty()} />,
        cssClass: "icon-column"
    } as TableHeaderInfo,
    ...TABLE_HEADERS
  ];

  const formattedData = useMemo(() => (
    roles?.filter(ar => !getSecurityRole(ar.userRoleTypeId)?.scopeRole || ar.orgScope)
      .map(ar => ({
        ...ar,
        roleName: getSecurityRole(ar.userRoleTypeId)?.desc,
        orgName: ar.orgScope?.name ?? ''
      } as RoleInfo))
      .filter(r => r.roleName)
      .sort((a, b) => a.roleName.localeCompare(b.roleName))
  ), [roles, getSecurityRole]);

  const [sortedData, setSortedData] = useState<RoleInfo[]>([]);

  return (
    <Table striped className="fixed" id="user-roles">
      <TableHeaders headers={tableHeaders} sortableData={formattedData} onSort={setSortedData} />

      {roles.length > 0
        ?
        <tbody>
          {sortedData.map(assignedRole =>
            <tr key={assignedRole.key}>
              <th scope="row" aria-label={'Select ' + assignedRole.roleName}>
                <Input type="checkbox" value={assignedRole.key}
                  aria-label={'Select ' + assignedRole.roleName}
                  checked={checkGroup.isChecked(assignedRole.key)}
                  onChange={event => checkGroup.onCheck(event, assignedRole.key)} />
              </th>
              <th scope="row">{assignedRole.roleName}</th>
              <td>
                <OmsToolTip orgId={assignedRole.orgScope?.id}>
                  {assignedRole.orgName}
                </OmsToolTip>
              </td>
            </tr>
          )}
        </tbody>
        :
        <tfoot>
          <tr>
            <td colSpan={tableHeaders.length} className="text-center">
              Add a user role to see it here.
            </td>
          </tr>
        </tfoot>
      }
    </Table>
  );
};
